<template>
  <div class="aiw2-hero">
    <div class="hero-left">
      <div class="hero-title-row">
        <h1>
          {{ title }}
          <v-tooltip bottom color="transparent">
            <template v-slot:activator="{ on, attrs }">
              <span class="info-ico" v-bind="attrs" v-on="on"></span>
            </template>
            <div class="white-bg-tooltip small-tooltip">{{ infoTitle }}</div>
          </v-tooltip>
          <span v-if="!hideMode" class="mode-pill"><span class="ico">{{ modeIcon }}</span> {{ modeLabel }}</span>
          <slot name="pill"></slot>
        </h1>
        <span v-if="keyword" class="kw-pill">{{ keyword }}</span>
      </div>
      <div v-if="reportId || hasTail" class="hero-meta">
        <span v-if="reportId">Report ID: {{ reportId }}</span>
        <span v-if="reportId && hasTail" class="dot"></span>
        <!-- Per-step summary tail (SEO / Publish): dot-separated items -->
        <template v-if="metaItems.length">
          <template v-for="(item, i) in metaItems">
            <span v-if="i > 0" :key="'d' + i" class="dot"></span>
            <span :key="i">{{ item }}</span>
          </template>
        </template>
        <!-- Default tail (Setup / Title / Draft): created date + page link -->
        <template v-else>
          <span v-if="createdLabel">Created on {{ createdLabel }}</span>
          <span v-if="createdLabel && pageLink" class="dot"></span>
          <a
            v-if="pageLink"
            class="ext"
            :href="pageLink.href"
            :title="pageLink.href"
            target="_blank"
            rel="noopener noreferrer"
          >{{ pageLink.label }} <span class="ext-arrow">↗</span></a>
        </template>
        <!-- Report details popover (design handoff) — same links as the old
             content-brief box under "Update optimization score". Publish step only. -->
        <template v-if="reportId && reportDetails">
          <span class="dot"></span>
          <report-details-popover :page-run="pageRun" :ai-run-id="aiRunId" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// Shared step hero — mirrors the design's .hero (blue-gradient banner with H1 +
// info icon + mode pill, a report-meta row, and the keyword pill).
import { MODES } from "../constants";
import ReportDetailsPopover from "./ReportDetailsPopover.vue";

export default {
  name: "AiwV2Hero",
  components: { ReportDetailsPopover },
  props: {
    title: { type: String, required: true },
    keyword: { type: String, default: "" },
    mode: { type: String, default: MODES.GUIDED },
    pageRun: { type: Object, default: () => ({}) },
    hideMode: { type: Boolean, default: false },
    infoTitle: { type: String, default: "Your POP report - these settings shape the article POP writes." },
    // Per-step meta tail. When non-empty, these dot-separated items replace the
    // default "Created on … · page link" tail (SEO/Publish pass a summary line).
    metaItems: { type: Array, default: () => [] },
    // Show the "Report details" popover in the meta row (Publish step only).
    reportDetails: { type: Boolean, default: false },
    // Finished run id, forwarded to the popover so "Open in content editor"
    // pre-loads THIS run's article (Publish step only).
    aiRunId: { default: null },
  },
  computed: {
    modeIcon() {
      return this.mode === MODES.FAST ? "⚡" : "✎";
    },
    modeLabel() {
      return this.mode === MODES.FAST ? "FAST MODE" : "GUIDED MODE";
    },
    reportId() {
      return (this.pageRun && (this.pageRun.id || this.pageRun.reportId)) || "";
    },
    createdLabel() {
      const raw = this.pageRun && (this.pageRun.createdAt || this.pageRun.created_at);
      if (!raw) return "";
      const d = new Date(raw);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    },
    // External link to the page being optimized — { href, label } where label is
    // the URL pathname (e.g. "/blog/dolomites"). Null when no URL is available.
    pageLink() {
      const url = this.pageRun && this.pageRun.url;
      if (!url) return null;
      let label = "/";
      try {
        const u = new URL(url.indexOf("http") === 0 ? url : "https://" + url);
        label = u.pathname || "/";
      } catch (e) { /* keep default path */ }
      return { href: url, label };
    },
    // Whether anything renders after the Report ID (drives the leading dot).
    hasTail() {
      return this.metaItems.length > 0 || !!this.createdLabel || !!this.pageLink;
    },
  },
};
</script>

<style scoped>
.aiw2-hero {
  background: linear-gradient(180deg, var(--pop-blue-soft, #c7ddff) 0%, var(--pop-blue-tint, #e9f3ff) 100%);
  border-radius: var(--r-xl, 14px);
  padding: 24px 28px;
  margin-bottom: 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}
/* Full width, so the title row's space-between still pushes the keyword pill to
   the far right (the outer flex used to do that). */
.hero-left { flex: 1 1 auto; min-width: 0; }
/* Title and keyword pill share one row; .hero-meta sits below on its own line, so
   a long meta line can never push the pill under it. */
.hero-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
.hero-left h1 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--text, #052e33);
  letter-spacing: -.015em;
  min-width: 0;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.info-ico {
  width: 16px; height: 16px;
  display: inline-block; flex-shrink: 0;
  vertical-align: middle; cursor: pointer; user-select: none;
  background-color: #014751; /* match content-brief info icon (mainColor) */
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat;
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat;
}
.mode-pill {
  display: inline-flex; align-items: center; gap: 5px;
  background: #fff;
  border: 1px solid var(--border, #e4e4e4);
  padding: 3px 10px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-2, #014751);
  margin-left: 4px;
}
.mode-pill .ico { font-size: 12px; }
.hero-meta {
  margin-top: 8px;
  font-size: 13px;
  color: var(--text-3, #6b7b7e);
  display: flex; gap: 14px; align-items: center; flex-wrap: wrap;
}
.hero-meta .dot { width: 4px; height: 4px; border-radius: 50%; background: var(--text-muted, #9aa6a8); }
.hero-meta .ext { color: var(--pop-blue, #316bff); text-decoration: none; font-weight: 500; }
/* U+2197 isn't in the (Google Fonts) Mulish subset, so it falls back to a system
   glyph that renders small; give it an explicit size so it matches the design. */
.hero-meta .ext-arrow { font-size: 13px; line-height: 1; font-family: 'Mulish', system-ui, sans-serif }
.kw-pill {
  background: var(--pop-orange-soft, #ffeddd);
  color: var(--pop-orange-deep, #b23d00);
  border: 1px solid var(--pop-orange-tint, #ffd9c2);
  padding: 8px 14px;
  border-radius: 7px;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  flex: 0 0 auto;
}
</style>
